import {SearchUtil, Util} from 'yougile-util'
import {DataObjectId, Hub, User} from 'yougile-model'
import {Items} from '@api/model/items'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {DepartmentsClient} from '@client/departments-client'
import {EmployeeSettings} from '@client/employee-settings'
import {PaymentClient} from '@client/payment-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Board} from '@extensions/board'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {UserAccess} from '@extensions/user-access'
import {UserUtils} from '@extensions/user-utils'
import {CompanyProject} from './company-projects/company-projects-store'
import {extractUserFields, UserFields} from './user-fields'

export const isBlocked = (userId: DataObjectId): boolean => {
  return PaymentClient.isUserDisallowed(globals.getCompanyId(), userId)
}

export const isUserAdmin = (user: User): boolean => {
  const box = Sync.getCurrentSandbox()
  return (box.users.get(user.id) || {isAdmin: false}).isAdmin
}

export const isAdmin = (): boolean => {
  return PermissionsChecker.isAdmin()
}

export const isProjectMine = (project: Hub): boolean => {
  return PermissionsChecker.checkShow('ignore-departments', project, {
    ignoreAdmin: true
  })
}

export const canEdit = (project: Hub): boolean => {
  return (
    isAdmin() ||
    UserAccess.getRoleInProject(globals.currentUserId, project) === 'admin'
  )
}

export const canAddUser = (): boolean =>
  UserAccess.canInviteUser(globals.getCompanyId())

export const canAdd = (): boolean => {
  const box = Sync.getCurrentSandbox()
  const company = box.get(box.companyId) as Hub
  return PermissionsChecker.checkAddChildTo(company)
}

export const canInvite = (project: Hub): boolean => {
  return UserAccess.canChangeUserInProject(project)
}

export const canSeeEmails = (): boolean => {
  const box = Sync.getCurrentSandbox()
  return CompanyPreferencesClient.isAllowedForCurrentUser(
    box.companyId,
    'seeEmails'
  )
}

export const listProjects = (opts?: {
  isArchived: boolean
  showOnlyUserProjects: boolean
}): Hub[] => {
  const companyId = globals.getCompanyId()
  let res = Project.listProjects(
    companyId,
    opts?.isArchived ? 'archive' : 'not-archive',
    opts?.showOnlyUserProjects ?? true
  )
  res = Items.list(Items.get(companyId), res.map(Items.fromHub)).map(
    Items.toHub
  )
  res = Util.removeDuplicateIds(res)
  return res
}

export const listForAdmin = (opts?: {isArchived: boolean}): Hub[] => {
  const companyId = globals.getCompanyId()
  const user = new Set(
    Project.listProjects(
      companyId,
      opts?.isArchived ? 'archive' : 'not-archive',
      true
    ).map(p => p.id)
  )
  let res = Project.listProjects(
    companyId,
    opts?.isArchived ? 'archive' : 'not-archive',
    false
  ).filter(p => !user.has(p.id))
  res = Items.list(Items.get(companyId), res.map(Items.fromHub)).map(
    Items.toHub
  )
  res = Util.removeDuplicateIds(res)
  return res
}

export const getDepartments = (user: User): string => {
  const box = Sync.getCurrentSandbox()
  return DepartmentsClient.listForUser(box, user.id)
    .map(d => d.title)
    .join(', ')
}

export const getPosition = (user: User): string =>
  EmployeeSettings.getPosition(user.id)

export const getAdmins = (): UserFields[] => {
  const box = Sync.getCurrentSandbox()
  return UserUtils.sortByName(box.getAllUsers(false, undefined, true))
    .filter(u => (box.users.get(u.id) || {isAdmin: false}).isAdmin)
    .map(extractUserFields)
}

export const getUsers = (): UserFields[] => {
  const box = Sync.getCurrentSandbox()
  const notAdmins = UserUtils.sortByName(
    box.getAllUsers(false, undefined, true)
  ).filter(u => !(box.users.get(u.id) || {isAdmin: false}).isAdmin)
  if (isAdmin()) {
    return notAdmins
      .filter(u => UserAccess.listProjectsForUser(box, u).length > 0)
      .map(extractUserFields)
  } else {
    return notAdmins.map(extractUserFields)
  }
}

export const getNotDistributed = (): UserFields[] => {
  if (isAdmin()) {
    const box = Sync.getCurrentSandbox()
    return UserUtils.sortByName(box.getAllUsers(false))
      .filter(u => !(box.users.get(u.id) || {isAdmin: false}).isAdmin)
      .filter(u => UserAccess.listProjectsForUser(box, u).length === 0)
      .map(extractUserFields)
  } else {
    return []
  }
}

export const getOpenedTasksCount = (project: Hub): number => {
  let tasksCount = 0

  Project.listBoards(project).forEach(board => {
    tasksCount = tasksCount + Board.getOpenedTasksData(board).length
  })

  return tasksCount
}

export const filterProjects = (
  list: CompanyProject[],
  filterQuery: string
): CompanyProject[] => {
  const words = SearchUtil.prepareWords(SearchUtil.cleanStr(filterQuery))
  const res: CompanyProject[] = []
  list.forEach(project => {
    const name = SearchUtil.cleanStr(project.hub.title)
    if (SearchUtil.hasAllWords(name, words)) {
      res.push(project)
      return
    }
  })
  return res
}

export const getLicenseUserCount = (): number => {
  const companyId = globals.getCompanyId()
  const payStatus = PaymentClient.get(companyId)
  const isFree = PaymentClient.isFree(companyId)
  return 10 + (isFree ? 0 : payStatus.users)
}

export const getUserCount = (): number => {
  const companyId = globals.getCompanyId()
  const users = PaymentClient.getUsers(companyId)
  return Object.keys(users).length
}
